import React from 'react';
import { Global, css } from '@emotion/react';

const GlobalStyles: React.FC = () => (
  <Global
    styles={css`
      :root {
        --app-header-height: 64px;
        /* Safe area spremenljivke za iPhone z zarezami */
        --safe-area-top: env(safe-area-inset-top, 0px);
        --safe-area-bottom: env(safe-area-inset-bottom, 0px);

        /* LIGHT MODE (Privzeto) */
        --bg-app: #f4f6fb;
        --bg-paper: #ffffff;
        --text-primary: #111827;
        --text-secondary: #6b7280;
        --border-color: #e5e7eb;
        --primary-color: #6366f1;
        --shadow-color: rgba(15, 23, 42, 0.1);

        --logo-filter: none;

        --app-bg-gradient:
          radial-gradient(circle at top right, rgba(99, 102, 241, 0.25), transparent 45%),
          radial-gradient(circle at 20% 20%, rgba(59, 130, 246, 0.15), transparent 40%),
          linear-gradient(180deg, rgba(240, 242, 255, 0.7) 0%, rgba(247, 250, 255, 0.95) 100%);
        --app-glow-1: rgba(99, 102, 241, 0.4);
        --app-glow-2: rgba(99, 102, 241, 0.3);
      }

      [data-theme='dark'] {
        --bg-app: #0f172a;
        --bg-paper: #1e293b;
        --text-primary: #f8fafc;
        --text-secondary: #94a3b8;
        --border-color: #334155;
        --primary-color: #818cf8;
        --shadow-color: rgba(0, 0, 0, 0.5);

        --logo-filter: brightness(0) invert(1);

        --app-bg-gradient:
          radial-gradient(circle at top right, rgba(99, 102, 241, 0.15), transparent 45%),
          radial-gradient(circle at 20% 20%, rgba(59, 130, 246, 0.1), transparent 40%),
          linear-gradient(180deg, #0f172a 0%, #1e293b 100%);
        --app-glow-1: rgba(99, 102, 241, 0.18);
        --app-glow-2: rgba(59, 130, 246, 0.12);
      }

      * {
        box-sizing: border-box;
        -webkit-tap-highlight-color: transparent;
      }

      html,
      body {
        margin: 0;
        font-family: 'Inter', 'Helvetica Neue', Helvetica, Arial, sans-serif;
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
        /* Prepreči scrollanje celotne strani na mobile */
        overscroll-behavior: none;
        overflow: hidden;
        /* Uporabi dynamic viewport height */
        height: 100vh;
        height: 100dvh;
        background: var(--app-bg-gradient);
        color: var(--text-primary);
      }

      #root {
        height: 100%;
        height: 100dvh;
        display: flex;
        flex-direction: column;
      }

      /* Prepreči zoom na inputih na iOS (font mora biti vsaj 16px) */
      input,
      select,
      textarea {
        font-size: 16px !important;
      }

      /* Scrollbar styling */
      ::-webkit-scrollbar {
        width: 6px;
        height: 6px;
      }

      ::-webkit-scrollbar-thumb {
        background: #cbd5e1;
        border-radius: 3px;
      }

      code {
        font-family: source-code-pro, Menlo, Monaco, Consolas, 'Courier New', monospace;
      }

      #root {
        display: flex;
        flex-direction: column;
        height: 100%;
      }

      .app-header {
        flex: 0 0 var(--app-header-height);
        width: 100%;
        z-index: 100;
        position: sticky;
        top: 0;
      }

      .app-shell {
        flex: 1;
        display: flex;
        flex-direction: column;
        background: var(--app-bg-gradient);
        position: relative;
        overflow: hidden;
        width: 100%;
        min-height: calc(100vh - var(--app-header-height));
        margin-top: 0;
      }

      .app-shell--fullscreen {
        position: fixed !important;
        top: var(--app-header-height) !important;
        left: 0 !important;
        right: 0 !important;
        bottom: 0 !important;
        width: 100% !important;
        height: calc(100vh - var(--app-header-height)) !important;
        max-width: 100% !important;
        border-radius: 0 !important;
        z-index: 1000 !important;
        overflow: auto !important;
        padding: 0 !important;
      }

      .app-shell::before,
      .app-shell::after {
        content: '';
        position: absolute;
        width: 360px;
        height: 360px;
        border-radius: 50%;
        pointer-events: none;
        filter: blur(30px);
        opacity: 0.8;
      }

      .app-shell::before {
        top: -140px;
        right: -100px;
        background: var(--app-glow-1);
      }

      .app-shell::after {
        bottom: -120px;
        left: -140px;
        background: var(--app-glow-2);
      }

      .app-content {
        flex: 1;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 2rem 1rem;
        position: relative;
        z-index: 1;
        width: 100%;
        min-height: calc(100vh - var(--app-header-height));
        box-sizing: border-box;
        overflow: auto;
      }

      /* Global body/html styles for fullscreen */
      body.app-fullscreen-active,
      html.app-fullscreen-active {
        overflow: hidden !important;
        margin: 0 !important;
        padding: 0 !important;
        height: 100%;
      }

      .app-stage {
        width: 100%;
        max-width: 1200px;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        flex: 1;
        padding: 0;
        margin: 0 auto;
        height: 100%;
      }

      .app-stage__content {
        width: 100%;
        height: 100%;
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .fallback {
        padding: 1rem 2rem;
        border-radius: 12px;
        background: var(--bg-paper);
        box-shadow: 0 10px 35px var(--shadow-color);
        font-weight: 500;
        color: var(--text-secondary);
      }

      @media (max-width: 768px) {
        body {
          overflow: auto !important;
          height: auto;
        }

        .app-shell {
          min-height: calc(100vh - var(--app-header-height));
          height: auto;
        }

        .app-content {
          padding: 1rem;
          width: 100%;
        }

        .app-stage {
          width: 100%;
          margin-top: 0.5rem;
          padding-bottom: 1rem;
        }

        .app-shell::before,
        .app-shell::after {
          display: none;
        }
      }

      /* Skeleton Loader Styles */
      .skeleton-loader {
        width: 100%;
        padding: 20px;
        background-color: var(--bg-app);
        border-radius: 8px;
        box-shadow: 0 2px 4px var(--shadow-color);
      }

      .skeleton-line {
        background: linear-gradient(
          90deg,
          rgba(148, 163, 184, 0.2) 25%,
          rgba(148, 163, 184, 0.35) 50%,
          rgba(148, 163, 184, 0.2) 75%
        );
        background-size: 200% 100%;
        animation: loading 1.5s infinite;
        border-radius: 4px;
        margin-bottom: 10px;
      }

      .skeleton-line.short {
        width: 40%;
        height: 15px;
      }

      .skeleton-line.medium {
        width: 70%;
        height: 15px;
      }

      .skeleton-line.long {
        width: 90%;
        height: 15px;
      }

      @keyframes loading {
        0% {
          background-position: -200% 0;
        }
        100% {
          background-position: 200% 0;
        }
      }
    `}
  />
);

export default GlobalStyles;
